import {
  armorInfo,
  damageStack,
  isItem,
  makeStack,
  maxDurability,
  stackLimit,
  type ArmorSlot,
  type ItemStack,
} from "../world/items";

/**
 * Slot-based survival inventory.
 *
 * The first `HOTBAR_SIZE` slots are the hotbar; the rest is storage. Stacks
 * never exceed their limit, empty stacks are removed, and anything that cannot
 * fit is parked in `overflow` and flushed automatically the moment a slot
 * frees — so a full pack never silently destroys a drop.
 */

export const HOTBAR_SIZE = 9;
export const STORAGE_ROWS = 3;
export const STORAGE_SIZE = HOTBAR_SIZE * STORAGE_ROWS;
export const INVENTORY_SIZE = HOTBAR_SIZE + STORAGE_SIZE;

export const ARMOR_SLOT_COUNT = 4;
export const ARMOR_HELMET = 0;
export const ARMOR_CHESTPLATE = 1;
export const ARMOR_LEGGINGS = 2;
export const ARMOR_BOOTS = 3;

export const ARMOR_SLOT_NAMES: readonly ArmorSlot[] = [
  "helmet",
  "chestplate",
  "leggings",
  "boots",
];

export class Inventory {
  readonly slots: (ItemStack | null)[] = new Array(INVENTORY_SIZE).fill(null);
  readonly armor: (ItemStack | null)[] = new Array(ARMOR_SLOT_COUNT).fill(null);
  /** Items waiting for a free slot; flushed automatically on every change. */
  readonly overflow: ItemStack[] = [];

  /** Total armor defense points across all 4 armor slots (0..20). */
  get totalArmorDefense(): number {
    let total = 0;
    for (const stack of this.armor) {
      if (!stack) continue;
      const info = armorInfo(stack.id);
      if (info) total += info.defense;
    }
    return total;
  }

  /**
   * Damages each equipped piece of armor.
   * In Minecraft 1.8.8, armor takes Math.max(1, Math.floor(damageAmount / 4.0)) durability damage.
   * Returns true if any armor piece broke.
   */
  damageArmor(damageAmount: number): boolean {
    const wear = Math.max(1, Math.floor(damageAmount / 4.0));
    let brokeAny = false;
    for (let i = 0; i < ARMOR_SLOT_COUNT; i++) {
      const stack = this.armor[i];
      if (!stack) continue;
      const next = damageStack(stack, wear);
      this.armor[i] = next;
      if (!next) brokeAny = true;
    }
    return brokeAny;
  }

  setArmorSlot(index: number, stack: ItemStack | null): void {
    if (index < 0 || index >= ARMOR_SLOT_COUNT) return;
    if (!stack || stack.count <= 0) {
      this.armor[index] = null;
      return;
    }
    const targetSlot = ARMOR_SLOT_NAMES[index];
    const info = armorInfo(stack.id);
    if (!info || info.slot !== targetSlot) return;
    this.armor[index] = { ...stack, count: 1 };
  }

  clickArmorSlot(
    index: number,
    button: "left" | "right",
    held: ItemStack | null,
  ): ItemStack | null {
    if (index < 0 || index >= ARMOR_SLOT_COUNT) return held;
    const targetSlot = ARMOR_SLOT_NAMES[index];
    if (held) {
      const info = armorInfo(held.id);
      if (!info || info.slot !== targetSlot) return held;
    }
    const current = this.armor[index];
    if (button === "right" && !held && current) {
      this.armor[index] = null;
      return current;
    }
    if (held) {
      this.armor[index] = { ...held, count: 1 };
      return current;
    }
    this.armor[index] = null;
    return current;
  }

  equipArmorFromSlot(slotIndex: number): boolean {
    const stack = this.slots[slotIndex];
    if (!stack) return false;
    const info = armorInfo(stack.id);
    if (!info) return false;
    const armorIndex = ARMOR_SLOT_NAMES.indexOf(info.slot);
    if (armorIndex === -1) return false;

    const previous = this.armor[armorIndex];
    this.armor[armorIndex] = { ...stack, count: 1 };
    this.slots[slotIndex] = previous;
    this.flushOverflow();
    return true;
  }

  quickMoveArmor(armorIndex: number): boolean {
    const stack = this.armor[armorIndex];
    if (!stack) return false;
    const left = this.placeIntoRegion(stack, 0, INVENTORY_SIZE);
    if (left === 0) {
      this.armor[armorIndex] = null;
      return true;
    }
    if (left < stack.count) {
      this.armor[armorIndex] = { ...stack, count: left };
      return true;
    }
    return false;
  }

  /** Occupied slot count. */
  get size(): number {
    let n = 0;
    for (const slot of this.slots) if (slot) n++;
    return n;
  }

  /** Total owned, including anything parked in the overflow. */
  count(id: number): number {
    if (id <= 0) return 0;
    let n = 0;
    for (const slot of this.slots) if (slot && slot.id === id) n += slot.count;
    for (const stack of this.overflow) if (stack.id === id) n += stack.count;
    return n;
  }

  countInSlot(index: number): number {
    return this.slots[index]?.count ?? 0;
  }

  has(id: number, n = 1): boolean {
    return this.count(id) >= n;
  }

  /** Add items: merge into existing stacks, then fill empty slots. */
  add(id: number, count = 1): void {
    const qty = Math.floor(count);
    if (!isItem(id) || !Number.isFinite(count) || qty <= 0) return;

    const max = maxDurability(id);
    if (max > 0) {
      // Tools carry per-instance state, so add them one at a time.
      for (let i = 0; i < qty; i++) this.addStack(makeStack(id));
      return;
    }
    this.addStack({ id, count: qty });
  }

  /** Add an existing stack, preserving tool durability. */
  addStack(stack: ItemStack): void {
    const qty = Math.floor(stack.count);
    if (!isItem(stack.id) || !Number.isFinite(stack.count) || qty <= 0) return;

    if (maxDurability(stack.id) > 0 && qty > 1) {
      for (let i = 0; i < qty; i++) this.addStack({ ...stack, count: 1 });
      return;
    }

    this.flushOverflow();
    const incoming = qty === stack.count ? stack : { ...stack, count: qty };
    const left = this.placeIntoRegion(incoming, 0, INVENTORY_SIZE);
    if (left > 0) this.overflow.push({ ...incoming, count: left });
  }

  /** Overwrite a slot (used by the creative palette and the screen). */
  setSlot(index: number, stack: ItemStack | null): void {
    if (index < 0 || index >= INVENTORY_SIZE) return;
    if (!stack || stack.count <= 0) {
      this.slots[index] = null;
    } else {
      const count = Math.min(Math.floor(stack.count), stackLimit(stack.id));
      this.slots[index] = { ...stack, count };
    }
    this.flushOverflow();
  }

  /** Empty a slot and return its stack. */
  removeSlot(index: number): ItemStack | null {
    const stack = this.slots[index] ?? null;
    if (stack) this.slots[index] = null;
    this.flushOverflow();
    return stack;
  }

  /** Consume exactly one item from a slot, for placing. */
  takeOne(index: number): boolean {
    const stack = this.slots[index];
    if (!stack) return false;
    this.slots[index] = stack.count <= 1 ? null : { ...stack, count: stack.count - 1 };
    this.flushOverflow();
    return true;
  }

  /** Remove `n` of `id` from anywhere. Fails (and changes nothing) if short. */
  take(id: number, n = 1): boolean {
    if (id <= 0 || n <= 0 || this.count(id) < n) return false;
    let left = n;
    for (let i = 0; i < INVENTORY_SIZE && left > 0; i++) {
      const slot = this.slots[i];
      if (!slot || slot.id !== id) continue;
      const moved = Math.min(slot.count, left);
      left -= moved;
      this.slots[i] = slot.count - moved > 0 ? { ...slot, count: slot.count - moved } : null;
    }
    for (let i = 0; i < this.overflow.length && left > 0; i++) {
      const slot = this.overflow[i];
      if (slot.id !== id) continue;
      const moved = Math.min(slot.count, left);
      left -= moved;
      if (slot.count - moved > 0) this.overflow[i] = { ...slot, count: slot.count - moved };
      else this.overflow.splice(i--, 1);
    }
    this.flushOverflow();
    return true;
  }

  /** Right-click empty-handed: pick up half the stack (rounded up). */
  splitSlot(index: number): ItemStack | null {
    return this.clickSlot(index, "right", null);
  }

  /**
   * Place a held stack onto a slot: fill an empty slot, merge into the same
   * item, or swap. Returns whatever the cursor still carries.
   */
  putStack(index: number, stack: ItemStack | null): ItemStack | null {
    if (!stack) return null;
    return this.clickSlot(index, "left", stack);
  }

  /** Apply one click of the cursor stack to a slot; returns the new cursor. */
  clickSlot(index: number, button: "left" | "right", held: ItemStack | null): ItemStack | null {
    const next = clickStack(this.slots, index, button, held);
    this.flushOverflow();
    return next;
  }

  /**
   * Shift-click: hotbar stacks move to storage and vice versa, merging into
   * existing stacks before taking an empty slot. Anything that does not fit
   * stays where it was.
   */
  quickMove(index: number): boolean {
    const stack = this.slots[index] ?? null;
    if (!stack) return false;

    // If this is armor and the matching equipment slot is empty, equip it
    const aInfo = armorInfo(stack.id);
    if (aInfo) {
      const aIndex = ARMOR_SLOT_NAMES.indexOf(aInfo.slot);
      if (aIndex !== -1 && !this.armor[aIndex]) {
        this.armor[aIndex] = { ...stack, count: 1 };
        this.slots[index] = stack.count > 1 ? { ...stack, count: stack.count - 1 } : null;
        this.flushOverflow();
        return true;
      }
    }

    const hotbar = index < HOTBAR_SIZE;
    const start = hotbar ? HOTBAR_SIZE : 0;
    const end = hotbar ? INVENTORY_SIZE : HOTBAR_SIZE;
    this.slots[index] = null;

    const left = this.placeIntoRegion(stack, start, end);
    if (left > 0) this.slots[index] = { ...stack, count: left };
    this.flushOverflow();
    return left < stack.count;
  }

  hotbar(): (ItemStack | null)[] {
    return this.slots.slice(0, HOTBAR_SIZE);
  }

  /** Slot number inside the hotbar, or -1. */
  hotbarIndexOf(id: number): number {
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      if (this.slots[i]?.id === id) return i;
    }
    return -1;
  }

  /** First slot holding the item, hotbar first, or -1. */
  firstSlotWith(id: number): number {
    for (let i = 0; i < INVENTORY_SIZE; i++) {
      if (this.slots[i]?.id === id) return i;
    }
    return -1;
  }

  /** Owned totals per item, including overflow, sorted by id. */
  entries(): [number, number][] {
    const totals = new Map<number, number>();
    for (const slot of this.slots) {
      if (!slot) continue;
      totals.set(slot.id, (totals.get(slot.id) ?? 0) + slot.count);
    }
    for (const stack of this.overflow) {
      totals.set(stack.id, (totals.get(stack.id) ?? 0) + stack.count);
    }
    return [...totals.entries()].sort((a, b) => a[0] - b[0]);
  }

  clear(): void {
    this.slots.fill(null);
    this.armor.fill(null);
    this.overflow.length = 0;
  }

  /** Merge then fill a slot range; returns how many items did not fit. */
  private placeIntoRegion(stack: ItemStack, start: number, end: number): number {
    const limit = stackLimit(stack.id);
    let left = stack.count;

    for (let i = start; i < end && left > 0; i++) {
      const slot = this.slots[i];
      if (!slot || slot.id !== stack.id) continue;
      const room = limit - slot.count;
      if (room <= 0) continue;
      const moved = Math.min(room, left);
      this.slots[i] = { ...slot, count: slot.count + moved };
      left -= moved;
    }
    for (let i = start; i < end && left > 0; i++) {
      if (this.slots[i]) continue;
      const moved = Math.min(limit, left);
      this.slots[i] = { ...stack, count: moved };
      left -= moved;
    }
    return left;
  }

  /** Move queued items into slots that have just freed up. */
  private flushOverflow(): void {
    if (this.overflow.length === 0) return;
    for (let i = this.overflow.length - 1; i >= 0; i--) {
      const stack = this.overflow[i];
      const left = this.placeIntoRegion(stack, 0, INVENTORY_SIZE);
      if (left <= 0) this.overflow.splice(i, 1);
      else this.overflow[i] = { ...stack, count: left };
    }
  }
}

/**
 * Cursor-stack click rules shared by the inventory and crafting grids.
 *
 * Left: take a whole stack, place one, merge, or swap.
 * Right: pick up half, place one, or merge one.
 */
export function clickStack(
  slots: (ItemStack | null)[],
  index: number,
  button: "left" | "right",
  held: ItemStack | null,
): ItemStack | null {
  if (index < 0 || index >= slots.length) return held;
  const slot = slots[index] ?? null;

  if (button === "right") {
    if (!held) {
      if (!slot) return null;
      const picked = Math.ceil(slot.count / 2);
      const rest = slot.count - picked;
      slots[index] = rest > 0 ? { ...slot, count: rest } : null;
      return { ...slot, count: picked };
    }
    if (!slot) {
      slots[index] = { ...held, count: 1 };
      return held.count > 1 ? { ...held, count: held.count - 1 } : null;
    }
    if (slot.id !== held.id || slot.count >= stackLimit(slot.id)) return held;
    slots[index] = { ...slot, count: slot.count + 1 };
    return held.count > 1 ? { ...held, count: held.count - 1 } : null;
  }

  if (!held) {
    if (!slot) return null;
    slots[index] = null;
    return slot;
  }
  if (!slot) {
    const limit = stackLimit(held.id);
    const placed = Math.min(held.count, limit);
    slots[index] = { ...held, count: placed };
    return held.count > placed ? { ...held, count: held.count - placed } : null;
  }
  if (slot.id === held.id) {
    const limit = stackLimit(slot.id);
    const room = limit - slot.count;
    if (room <= 0 && limit === 1) {
      // Unstackable items (tools) swap instead of merging, so each instance
      // keeps its own durability.
      slots[index] = { ...held, count: 1 };
      return slot;
    }
    const moved = Math.min(room, held.count);
    if (moved > 0) slots[index] = { ...slot, count: slot.count + moved };
    return held.count > moved ? { ...held, count: held.count - moved } : null;
  }

  // Different item: swap. Callers always pass a held stack within its limit.
  slots[index] = { ...held, count: Math.min(held.count, stackLimit(held.id)) };
  return slot;
}
